import { Injectable } from '@angular/core';
import { ComponentLoader, ComponentLoaderFactory } from 'ngx-bootstrap/component-loader';
import { SettingsConfirmModal } from './settings-confirm-modal';
import { SettingsConfirmModalDefaults, SettingsConfirmModalOptions } from './settings-confirm-modal.options';

let currentId = 7001;

/**
 * Opens `SettingsConfirmModal` and resolves once the user picks confirm/cancel (or dismisses via
 * the backdrop/close button). Same `ComponentLoaderFactory` primitive `SettingsSidePanelService`
 * uses, but with a `Promise<boolean>`-returning API instead of a handle — there's no content to
 * project and nothing for the caller to manage while it's open, unlike the side panel.
 */
@Injectable({
  providedIn: 'root',
})
export class SettingsConfirmModalService {
  private loaders: ComponentLoader<SettingsConfirmModal>[] = [];

  constructor(private clf: ComponentLoaderFactory) {}

  confirm(config: Partial<SettingsConfirmModalOptions>): Promise<boolean> {
    // Only one modal at a time — the trigger button can stay focused behind it, so repeated Enter would otherwise stack another.
    if (this.loaders.length > 0) {
      return Promise.resolve(false);
    }

    const id = config.id ?? currentId++;

    return new Promise<boolean>((resolve) => {
      let settled = false;
      const settle = (result: boolean) => {
        if (settled) {
          return;
        }
        settled = true;
        resolve(result);
      };

      const modalConfig: SettingsConfirmModalOptions = {
        ...SettingsConfirmModalDefaults,
        ...config,
        id,
        onConfirm: () => settle(true),
        onCancel: () => settle(false),
      } as SettingsConfirmModalOptions;

      const loader = this.clf.createLoader<SettingsConfirmModal>();
      this.loaders.push(loader);

      loader
        .provide({ provide: SettingsConfirmModalOptions, useValue: modalConfig })
        .attach(SettingsConfirmModal)
        .to('body')
        .show({});

      // Backdrop dismiss / close button also route through cancel() → onCancel → settle(false),
      // but onHidden is the backstop that guarantees the promise always resolves once the loader
      // actually tears down, however it got closed.
      loader.onHidden.subscribe(() => settle(false));
    });
  }

  /** Called by `SettingsConfirmModal.close()` once its close animation has finished. */
  remove(id?: number | string): void {
    const index = this.loaders.findIndex((loader) => (loader.instance?.config as any)?.id === id);
    const loader = index >= 0 ? this.loaders[index] : this.loaders[this.loaders.length - 1];
    if (!loader) {
      return;
    }
    loader.hide(id);
    if (index >= 0) {
      this.loaders.splice(index, 1);
    } else {
      this.loaders.pop();
    }
  }
}
